import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Clock, ArrowLeft, Share2 } from "lucide-react";
import { img, fmtDate, setMeta, ListenOn, AudioPlayer, PodcastCTA, useFeedLink, clean } from "@/components/PodcastParts";

const isoDuration = (d) => {
  const p = (d || "").split(":").map(Number);
  if (p.some(Number.isNaN) || !p.length) return undefined;
  const [h, m, s] = p.length === 3 ? p : [0, ...p];
  return `PT${h ? `${h}H` : ""}${m}M${s}S`;
};

function useEpisodeSeo(d) {
  useEffect(() => {
    if (!d) return undefined;
    const { episode: ep, show } = d;
    const title = `${ep.title} | Saving America Money Podcast`;
    const full = clean(ep.description);
    const desc = full.slice(0, 160);
    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:image", img(ep.image), "property");
    setMeta("og:type", "article", "property");
    setMeta("og:url", window.location.href, "property");
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.podcast = "1";
    script.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "PodcastEpisode", url: window.location.href, name: ep.title, description: full,
      datePublished: ep.pub_date?.slice(0, 10), episodeNumber: ep.episode || undefined, timeRequired: isoDuration(ep.duration), image: img(ep.image),
      associatedMedia: { "@type": "MediaObject", contentUrl: ep.audio_url },
      partOfSeries: { "@type": "PodcastSeries", name: show.title, url: `${window.location.origin}/podcast` },
      author: { "@type": "Person", name: show.host },
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [d]);
}

export default function PodcastEpisode() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null); setErr("");
    api.get(`/podcast/${slug}`).then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.status === 404 ? "We couldn't find that episode." : "The episode couldn't load right now."));
  }, [slug]);
  useEpisodeSeo(d);
  useFeedLink(d?.show?.feed_url);
  const share = async () => {
    const url = window.location.href;
    if (navigator.share) { try { await navigator.share({ title: d.episode.title, url }); return; } catch { /* closed */ } }
    await navigator.clipboard?.writeText(url); toast.success("Link copied");
  };
  if (err) return <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="episode-error"><p className="text-sam-slate">{err}</p><Link to="/podcast" className="mt-3 inline-block font-semibold text-sam-crimson underline">All episodes</Link></div>;
  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-sam-navy" /></div>;
  const { episode: ep, show } = d;
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10 sm:py-14" data-testid="episode-page">
      <Link to="/podcast" className="inline-flex items-center gap-1 text-sm font-semibold text-sam-navy hover:underline" data-testid="episode-back"><ArrowLeft className="h-4 w-4" />Saving America Money Podcast</Link>
      <div className="mt-6 grid gap-6 sm:grid-cols-[200px_1fr] sm:items-start">
        <img src={img(ep.image)} alt={ep.title} className="w-full max-w-[200px] rounded-2xl shadow-lg" data-testid="episode-cover" />
        <div>
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sam-crimson">
            {ep.episode && <span data-testid="episode-number">Episode {ep.episode}</span>}
            <span className="text-sam-slate" data-testid="episode-date">{fmtDate(ep.pub_date)}</span>
            {ep.duration && <span className="inline-flex items-center gap-1 text-sam-slate" data-testid="episode-duration"><Clock className="h-3 w-3" />{ep.duration}</span>}
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-tight text-sam-navy" data-testid="episode-title">{ep.title}</h1>
          <p className="mt-1 text-sm font-semibold text-sam-navy">Hosted by {show.host}</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={share} data-testid="episode-share"><Share2 className="h-4 w-4 mr-1" />Share this episode</Button>
        </div>
      </div>
      <div className="mt-6 rounded-2xl border border-border bg-white p-4"><AudioPlayer ep={ep} testid="episode-audio" /></div>
      <section className="mt-8">
        <h2 className="font-display text-base md:text-lg font-bold uppercase tracking-wider text-sam-navy">Show notes</h2>
        <div className="mt-3 space-y-3 text-sm sm:text-base leading-relaxed text-sam-slate" data-testid="episode-notes">{clean(ep.description).split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}</div>
      </section>
      <div className="mt-8"><ListenOn links={show.links} /></div>
      <div className="mt-10"><PodcastCTA /></div>
    </div>
  );
}
